import { UserPlus, Activity, Search, Share2, Award, CheckCircle2 } from 'lucide-react'
import { APPROVED_COMPETITION_EXAMPLES, COMPETITION_POLICY_NOTICE } from '../utils/competitions'

const STEPS = [
  {
    title: 'Create Your Profile',
    description: 'Sign up as a player, Scout, or Club. Each role gets a tailored dashboard and tools.',
    icon: UserPlus,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10'
  },
  {
    title: 'Build Your Career Profile',
    description: 'Players add personal details, stats, career history, match clips, achievements, and documents to build a complete football profile.',
    icon: Activity,
    color: 'text-green-500',
    bg: 'bg-green-500/10'
  },
  {
    title: 'Discover Talent',
    description: 'Scouts and Clubs search, filter, and explore player profiles by position, age, stats, availability, and match footage.',
    icon: Search,
    color: 'text-purple-500',
    bg: 'bg-purple-500/10'
  },
  {
    title: 'Connect & Communicate',
    description: 'Scouts and Clubs message players directly. Share profiles, send trial invitations, and start recruitment conversations.',
    icon: Share2,
    color: 'text-orange-500',
    bg: 'bg-orange-500/10'
  },
  {
    title: 'Recruit & Progress',
    description: 'Clubs send trial invitations. Scouts create detailed reports. Players track their progress and career milestones.',
    icon: Award,
    color: 'text-red-500',
    bg: 'bg-red-500/10'
  }
]

interface HowItWorksProps {
  onLoginClick: () => void
}

export default function HowItWorksPage({ onLoginClick }: HowItWorksProps) {
  return (
    <div className="min-h-screen bg-[#050910] text-white">
      <div className="max-w-5xl mx-auto px-4 py-20 space-y-24">
        {/* Header */}
        <section className="text-center space-y-4">
          <h1 className="text-4xl md:text-5xl font-display font-bold">The Scouta Process</h1>
          <p className="text-lg text-slate-200 max-w-2xl mx-auto">
            From creating a player profile to receiving a trial invitation — Scouta connects the entire football journey.
          </p>
          <div className="mx-auto max-w-3xl rounded-3xl border border-amber-400/20 bg-amber-400/10 px-6 py-5 text-left text-slate-100">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-amber-300">Competition policy</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-200">
              {COMPETITION_POLICY_NOTICE}
            </p>
            <p className="mt-3 text-sm text-slate-300">
              Approved examples include {APPROVED_COMPETITION_EXAMPLES.join(', ')} and other officially recognized local, national, or international competitions.
            </p>
          </div>
        </section>

        {/* Steps */}
        <div className="space-y-12 relative">
          {/* Vertical line connector (desktop) */}
          <div className="absolute left-[39px] top-8 bottom-8 w-px bg-white/5 hidden md:block" />

          {STEPS.map((step, index) => (
            <div key={step.title} className="flex flex-col md:flex-row gap-8 items-start relative group">
              <div className={`w-20 h-20 rounded-3xl ${step.bg} flex items-center justify-center shrink-0 z-10 transition-transform group-hover:scale-105 border border-white/5`}>
                <step.icon className={`w-10 h-10 ${step.color}`} />
              </div>
              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-4">
                  <span className="text-xs font-black uppercase tracking-[0.3em] text-slate-300">Step 0{index + 1}</span>
                  <div className="h-px w-8 bg-white/10" />
                </div>
                <h3 className="text-3xl font-bold">{step.title}</h3>
                <p className="text-slate-200 text-lg leading-relaxed max-w-2xl">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Features for different users */}
        <section className="grid md:grid-cols-3 gap-8">
           <div className="surface-panel-low p-10 rounded-3xl border border-white/10 space-y-6">
              <h3 className="text-2xl font-bold flex items-center gap-2">
                <CheckCircle2 className="text-green-500" />
                For Players
              </h3>
              <ul className="space-y-4 text-slate-100">
                <li className="flex gap-3"><span className="text-green-400">✓</span> Complete career profile with stats and history</li>
                <li className="flex gap-3"><span className="text-green-400">✓</span> Upload match clips and achievements</li>
                <li className="flex gap-3"><span className="text-green-400">✓</span> Control your discoverability settings</li>
                <li className="flex gap-3"><span className="text-green-400">✓</span> Receive trial invitations from clubs</li>
                <li className="flex gap-3"><span className="text-green-400">✓</span> Message with Scouts and Clubs</li>
              </ul>
           </div>
           <div className="surface-panel-low p-10 rounded-3xl border border-white/10 space-y-6">
              <h3 className="text-2xl font-bold flex items-center gap-2">
                <CheckCircle2 className="text-blue-500" />
                For Scouts
              </h3>
              <ul className="space-y-4 text-slate-100">
                <li className="flex gap-3"><span className="text-blue-400">✓</span> Advanced player search and filters</li>
                <li className="flex gap-3"><span className="text-blue-400">✓</span> Create detailed scouting reports</li>
                <li className="flex gap-3"><span className="text-blue-400">✓</span> Record live match observations</li>
                <li className="flex gap-3"><span className="text-blue-400">✓</span> Save and shortlist players</li>
                <li className="flex gap-3"><span className="text-blue-400">✓</span> Message players directly</li>
              </ul>
           </div>
           <div className="surface-panel-low p-10 rounded-3xl border border-white/10 space-y-6">
              <h3 className="text-2xl font-bold flex items-center gap-2">
                <CheckCircle2 className="text-purple-500" />
                For Clubs & Academies
              </h3>
              <ul className="space-y-4 text-slate-100">
                <li className="flex gap-3"><span className="text-purple-400">✓</span> Squad management by age category</li>
                <li className="flex gap-3"><span className="text-purple-400">✓</span> Send trial invitations to players</li>
                <li className="flex gap-3"><span className="text-purple-400">✓</span> Create and import player profiles</li>
                <li className="flex gap-3"><span className="text-purple-400">✓</span> Track recruitment pipeline</li>
                <li className="flex gap-3"><span className="text-purple-400">✓</span> Manage club verification status</li>
              </ul>
           </div>
        </section>

        {/* Closing CTA */}
        <div className="text-center bg-gradient-to-b from-[#0a151f] to-transparent p-12 rounded-[40px] border border-white/5">
           <button 
             onClick={onLoginClick}
             className="btn-primary px-12 py-5 text-lg font-bold"
           >
             Join Scouta
           </button>
        </div>
      </div>
    </div>
  )
}
